import type { SupabaseClient } from '@supabase/supabase-js';

/**
 * Pending referral from /registro when email confirmation is required (no session yet).
 * Flushed on first successful login when we have a session + usuarios.id.
 */
const STORAGE_KEY = 'clientelo_referral_pending';

export type PendingReferralPayload = {
  referral_code_id: string;
  tier: string;
  canal: 'link' | 'code';
};

export function savePendingReferral(p: PendingReferralPayload) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(p));
  } catch {
    /* ignore */
  }
}

export function clearPendingReferral() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

function readPending(): PendingReferralPayload | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as PendingReferralPayload;
  } catch {
    return null;
  }
}

/**
 * After login / session with usuarios row — create referrals row if user registered with ?ref= but email was unconfirmed.
 */
export async function flushPendingReferralAttribution(
  supabase: SupabaseClient,
  referredUsuarioId: string
): Promise<void> {
  const p = readPending();
  if (!p) return;
  try {
    const { error } = await supabase.from('referrals').insert({
      referral_code_id: p.referral_code_id,
      referred_usuario_id: referredUsuarioId,
      referred_empresa_id: null,
      tier: p.tier,
      canal_atribucion: p.canal,
      status: 'pending',
    } as never);
    if (error) {
      console.warn('Referral pending flush:', error.message);
    }
  } catch (e) {
    console.warn('Referral pending flush failed:', e);
  } finally {
    clearPendingReferral();
  }
}

export type ReferralLookupRow = {
  id: string;
  codigo: string;
  tier: string;
  referrer_nombre: string;
};

/**
 * Fire-and-forget referral row after signup when session exists.
 */
export async function insertReferralAfterSignup(
  supabase: SupabaseClient,
  args: {
    referredUsuarioId: string;
    referredEmpresaId: string | null;
    row: ReferralLookupRow;
    canal: 'link' | 'code';
  }
): Promise<void> {
  const { referredUsuarioId, referredEmpresaId, row, canal } = args;
  try {
    const { error } = await supabase.from('referrals').insert({
      referral_code_id: row.id,
      referred_usuario_id: referredUsuarioId,
      referred_empresa_id: referredEmpresaId,
      tier: row.tier,
      canal_atribucion: canal,
      status: 'pending',
    } as never);
    if (error) {
      console.error('Referral attribution failed:', error.message);
    }
  } catch (e) {
    console.error('Referral attribution failed:', e);
  }
}
